import { useQuery } from '@tanstack/react-query'
import { enqueueSnackbar } from '@applift/factor'

import {
  getAdCategoriesKey,
  getAgesKey,
  getAudiencesListKey,
  getCampaignAttachedCreativesInfoKey,
  getCampaignDetailKey,
  getCarriersKey,
  getConversionsInfoKey,
  getCountriesListKey,
  getCustomerConfigKey,
  getDeviceTypesKey,
  getEthnicitiesKey,
  getExchangesKey,
  getGenderKey,
  getIncomesKey,
  getInterestsKey,
  getInventoryGroupDetailsKey,
  getIOBudgetDistributionMethodKey,
  getIODetailKey,
  getLanguagesKey,
  getManufacturersKey,
  getNetworkTypesKey,
  getOsTypesKey,
  getPoliticalAdvertiserInfoKey,
  getPrebidProviderDetailsKey,
  getPreBidSegmentsKey,
  getStatesListKey,
  getTrafficTypesKey,
} from '../api/QueryKeys'
import { WithResponse } from '../models/Response'
import {
  getAudiencesList,
  getCampaignAttachedCreativesInfo,
  getConversionsInfo,
  getCountriesList,
  getInventoryGroupDetails,
  getIOBudgetDistributionMethod,
  getIODetails,
  getPreBidAudienceSegments,
  getPublisherCategories,
  getStatesList,
  getPreBidProviderDetails,
  getDeviceTypes,
  getNetworkTypes,
  getTrafficTypes,
  getOSTypes,
  getManufacturers,
  getCarriers,
  getAgeList,
  getLanguagesList,
  getInterestsList,
  getIncomesList,
  getGenderList,
  getEthnicitiesList,
  getAdvertiserList,
  getExchanges,
} from '../api/campaigns'
import { getCampaignDetails } from '../api/filter'
import { getCustomerConfig } from '../api/app'

const onError = (e: WithResponse) =>
  enqueueSnackbar(
    e?.errorObjects?.[0]?.error
      ? (e.errorObjects[0].error as string)
      : 'Something went wrong. Please try after sometime.',
    {
      variant: 'error',
    }
  )

export const useCampaignDetails = ({ id }: { id: number | null }) => {
  const data = useQuery(
    getCampaignDetailKey.keys('getCampaignDetails', Number(id)),
    getCampaignDetails,
    {
      onError,
      enabled: !(id == null || isNaN(id)),
    }
  )
  return data
}

export const useIODetail = (ioParam: { id: number }) => {
  const data = useQuery(
    getIODetailKey.keys('getIODetails', ioParam),
    getIODetails,
    {
      onError,
      enabled: !(ioParam.id == null || isNaN(ioParam.id)),
    }
  )
  return data
}

export const useIOBudgetDistributionInfo = () => {
  const data = useQuery(
    getIOBudgetDistributionMethodKey.keys('getIOBudgetDistributionMethod'),
    getIOBudgetDistributionMethod,
    {
      onError,
    }
  )
  return data
}

export const useCountriesList = (
  segmentIds: number[],
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getCountriesListKey.keys('getCountriesList', segmentIds),
    getCountriesList,
    {
      onError,
      enabled: options?.enabled,
    }
  )
  return data
}

export const useStatesList = (
  segmentIds: number[],
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getStatesListKey.keys('getStatesList', segmentIds),
    getStatesList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useCampaignCreativesInfo = (
  creativeParam: {
    campaignId: number
  },
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getCampaignAttachedCreativesInfoKey.keys(
      'getCampaignAttachedCreativesInfo',
      creativeParam
    ),
    getCampaignAttachedCreativesInfo,
    {
      onError,
      enabled: options?.enabled,
    }
  )
  return data
}

export const useCampaignAudiencesInfo = (
  audienceListParam: {
    owIds: string
    audienceTypeIds: string
  },
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getAudiencesListKey.keys('getAudiencesList', audienceListParam),
    getAudiencesList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useConversionInfo = (
  conversionIds: string,
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getConversionsInfoKey.keys('getConversionsInfo', conversionIds),
    getConversionsInfo,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const usePublisherCategoryInfo = (
  publisherCategoryIds: number[],
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getAdCategoriesKey.keys('getPublisherCategories', publisherCategoryIds),
    getPublisherCategories,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useInventoryGroupsInfo = (
  inventoryGroupParam: {
    groupIds: string
    pageNo: number
    includeContextualCount: boolean
  },
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getInventoryGroupDetailsKey.keys(
      'getInventoryGroupDetails',
      inventoryGroupParam
    ),
    getInventoryGroupDetails,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const usePrebidSegmentInfo = (
  prebidAudienceSegmentIdList: number[],
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getPreBidSegmentsKey.keys(
      'getPreBidAudienceSegments',
      prebidAudienceSegmentIdList
    ),
    getPreBidAudienceSegments,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const usePrebidProviderInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getPrebidProviderDetailsKey.keys('getPreBidProviderDetails'),
    getPreBidProviderDetails,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useNetworkTypesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getNetworkTypesKey.keys('getNetworkTypes'),
    getNetworkTypes,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useTrafficTypesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getTrafficTypesKey.keys('getTrafficTypes'),
    getTrafficTypes,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useOSTypesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(getOsTypesKey.keys('getOSTypes'), getOSTypes, {
    enabled: options?.enabled,
    onError,
  })
  return data
}

export const useManufacturersInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getManufacturersKey.keys('getManufacturers'),
    getManufacturers,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useDeviceTypesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getDeviceTypesKey.keys('getDeviceTypes'),
    getDeviceTypes,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useCarrierTypesInfo = (
  carriers: string,
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getCarriersKey.keys('getCarriers', carriers),
    getCarriers,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useAgesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(getAgesKey.keys('getAgeList'), getAgeList, {
    enabled: options?.enabled,
    onError,
  })
  return data
}

export const useLanguagesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getLanguagesKey.keys('getLanguagesList'),
    getLanguagesList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useInterestsInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getInterestsKey.keys('getInterestsList'),
    getInterestsList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useIncomeInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(getIncomesKey.keys('getIncomesList'), getIncomesList, {
    enabled: options?.enabled,
    onError,
  })
  return data
}

export const useGenderInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(getGenderKey.keys('getGenderList'), getGenderList, {
    enabled: options?.enabled,
    onError,
  })
  return data
}

export const useEthnicitiesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(
    getEthnicitiesKey.keys('getEthnicitiesList'),
    getEthnicitiesList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const usePoliticalAdvertiserInfo = (
  politicalAdvertiserId: string,
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getPoliticalAdvertiserInfoKey.keys(
      'getAdvertiserList',
      politicalAdvertiserId
    ),
    getAdvertiserList,
    {
      enabled: options?.enabled,
      onError,
    }
  )
  return data
}

export const useExchangesInfo = (options?: { enabled?: boolean }) => {
  const data = useQuery(getExchangesKey.keys('getExchanges'), getExchanges, {
    enabled: options?.enabled,
    onError,
  })
  return data
}

export const useCustomerConfigInfo = (
  customerOwId: string,
  options?: {
    enabled?: boolean
  }
) => {
  const data = useQuery(
    getCustomerConfigKey.keys('getCustomerConfig', customerOwId),
    getCustomerConfig,
    {
      enabled: options?.enabled,
      cacheTime: 5000,
      onError,
    }
  )
  return data
}
